// LiveCallPanel.jsx
// ★ LIVE CALL SCREEN-POP (owner request 2026-10-06): the moment the front desk
//   is joined to an auto-dialed lead, every logged-in Elyon user gets a panel
//   with that lead's details — name, phone, email, the service she asked
//   about, where she came from, and her history if she's already a client —
//   plus a "Book appointment" button that opens the booking form with her
//   already filled in. The desk no longer has to ask for everything again.
//
//   Booking hand-off: we stash the lead in window.__elyonPendingLeadBooking,
//   switch to CRM → Appointments, and fire "elyon:book-for-lead". The
//   Appointments screen picks it up (on mount or via the event) and opens
//   New Appointment with the client selected, or the new-client form
//   pre-filled if she isn't a client yet.

import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";

const C = {
  surface:"#ffffff", border:"#e8e2d8", gold:"#b48c3c", goldD:"#8a6a2a", goldBg:"rgba(180,140,60,0.08)",
  green:"#2d8a55", greenBg:"rgba(45,138,85,0.10)", red:"#b54040", muted:"#6b6356", text:"#1a1a2e", light:"#f4f0ea",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const last10 = (p) => String(p || "").replace(/\D/g, "").slice(-10);
function formatPhone(p) {
  const d = last10(p);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (p || "");
}
const titleCase = (s) => String(s || "").trim().toLowerCase().replace(/(^|[\s\-'])\p{L}/gu, (m) => m.toUpperCase());

export function startBookingForLead(info) {
  window.__elyonPendingLeadBooking = { ...info, at: Date.now() };
  window.dispatchEvent(new CustomEvent("elyon:goto-core"));
  window.dispatchEvent(new CustomEvent("elyon:goto-tab", { detail: "appointments" }));
  // Appointments may already be mounted — tell it directly too.
  setTimeout(() => window.dispatchEvent(new CustomEvent("elyon:book-for-lead")), 50);
}

export default function LiveCallPanel({ profile }) {
  const [calls, setCalls] = useState([]); // [{call, lead, client, lastAppt}]
  const [now, setNow] = useState(Date.now());
  const [copied, setCopied] = useState("");
  const seen = useRef(new Set());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!profile?.id) return;

    const enrich = async (call) => {
      let lead = null, client = null, lastAppt = null;
      if (call.lead_id) {
        const { data } = await supabase.from("leads")
          .select("id,full_name,first_name,phone,email,interest,source,ad_name,utm_campaign,created_at,notes,call_attempts,lead_temperature")
          .eq("id", call.lead_id).maybeSingle();
        lead = data;
      }
      const phone = lead?.phone || call.phone;
      const p10 = last10(phone);
      if (p10) {
        const { data } = await supabase.from("clients").select("id,name,phone,email,visits,spent,noshow").like("phone", `%${p10}%`).limit(1);
        client = data?.[0] || null;
      }
      if (!client && lead?.email) {
        const { data } = await supabase.from("clients").select("id,name,phone,email,visits,spent,noshow").eq("email", lead.email).limit(1);
        client = data?.[0] || null;
      }
      if (client) {
        const { data } = await supabase.from("appointments").select("date,treat,status").eq("cid", client.id)
          .neq("status", "cancelled").order("date", { ascending: false }).limit(1);
        lastAppt = data?.[0] || null;
      }
      return { call, lead, client, lastAppt };
    };

    const show = async (call) => {
      if (!call?.id || !call.staff_accepted_at) return;
      if (seen.current.has(call.id)) {
        // Already showing — just refresh its status (e.g. call ended).
        setCalls((cs) => cs.map((c) => (c.call.id === call.id ? { ...c, call: { ...c.call, ...call } } : c)));
        return;
      }
      seen.current.add(call.id);
      const item = await enrich(call);
      setCalls((cs) => [...cs, item]);
    };

    // A call that is already live when the page loads.
    (async () => {
      const since = new Date(Date.now() - 15 * 60000).toISOString();
      const { data } = await supabase.from("auto_dial_calls").select("*")
        .eq("status", "dialing").not("staff_accepted_at", "is", null).gte("started_at", since);
      (data || []).forEach(show);
    })();

    const ch = supabase.channel("live-call-panel-" + Math.random().toString(36).slice(2))
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "auto_dial_calls" }, (payload) => show(payload.new))
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [profile?.id]);

  if (!calls.length) return null;
  const item = calls[0];
  const { call, lead, client, lastAppt } = item;
  const name = titleCase(lead?.full_name || call.lead_name || "Lead");
  const phone = lead?.phone || call.phone;
  const email = lead?.email || client?.email || "";
  const interest = lead?.interest || call.interest || "";
  const live = call.status === "dialing";
  const secs = Math.max(0, Math.floor((now - new Date(call.staff_accepted_at).getTime()) / 1000));
  const dismiss = () => setCalls((cs) => cs.slice(1));
  const copy = (label, value) => {
    navigator.clipboard?.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(""), 1500);
  };
  const book = () => {
    startBookingForLead({ name, phone, email, interest, clientId: client?.id || null, leadId: lead?.id || null });
    dismiss();
  };

  const row = (label, value, copyable) => value ? (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: "1px solid " + C.light }}>
      <div style={{ width: 78, fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: C.text, wordBreak: "break-word" }}>{value}</div>
      {copyable && (
        <button onClick={() => copy(label, copyable)} style={{ border: "1px solid " + C.border, background: copied === label ? C.greenBg : C.surface, color: copied === label ? C.green : C.muted, borderRadius: 6, padding: "4px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: F }}>
          {copied === label ? "Copied" : "Copy"}
        </button>
      )}
    </div>
  ) : null;

  return (
    <div style={{ position: "fixed", right: 20, bottom: 20, zIndex: 3000, width: 380, maxWidth: "calc(100vw - 32px)", background: C.surface, borderRadius: 16, boxShadow: "0 20px 60px rgba(0,0,0,0.25)", border: "2px solid " + (live ? C.green : C.border), fontFamily: F, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", background: live ? C.green : C.muted, color: "#fff", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#fff", opacity: live ? 1 : 0.5, animation: live ? "lcpulse 1.2s infinite" : "none" }} />
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", flex: 1 }}>
          {live ? `LIVE CALL · ${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}` : "CALL ENDED"}
          {calls.length > 1 ? ` · +${calls.length - 1} more` : ""}
        </span>
        <button onClick={dismiss} title="Close" style={{ background: "transparent", border: "none", color: "#fff", fontSize: 18, cursor: "pointer", lineHeight: 1 }}>×</button>
      </div>
      <style>{"@keyframes lcpulse{0%,100%{opacity:1}50%{opacity:.3}}"}</style>

      <div style={{ padding: "14px 18px 6px" }}>
        <div style={{ fontSize: 22, fontWeight: 700, fontFamily: S, color: C.text }}>{name}</div>
        {interest && <div style={{ display: "inline-block", marginTop: 6, padding: "4px 10px", borderRadius: 20, background: C.goldBg, color: C.goldD, fontSize: 12, fontWeight: 700 }}>{interest}</div>}
        <div style={{ marginTop: 8 }}>
          {row("Phone", formatPhone(phone), phone)}
          {row("Email", email, email)}
          {row("Source", [lead?.source === "facebook" ? "Facebook ad" : lead?.source, lead?.ad_name || lead?.utm_campaign].filter(Boolean).join(" · "))}
          {row("Lead from", lead?.created_at ? new Date(lead.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "")}
          {client
            ? row("Client", `Existing · ${client.visits || 0} visit${client.visits === 1 ? "" : "s"}${client.noshow ? ` · ${client.noshow} no-show` : ""}${lastAppt ? ` · last: ${lastAppt.treat} (${lastAppt.date})` : ""}`)
            : row("Client", "New — not in the system yet")}
          {row("Notes", lead?.notes ? String(lead.notes).replace(/\[Re-engaged[^\]]*\]/g, "").trim() : "")}
        </div>
      </div>

      <div style={{ padding: "10px 18px 16px", display: "flex", gap: 8 }}>
        <button onClick={book} style={{ flex: 1, padding: "12px", background: "linear-gradient(135deg," + C.gold + "," + C.goldD + ")", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: F }}>
          📅 Book appointment
        </button>
      </div>
    </div>
  );
}
